import {
  Either, Fail, Left, Maybe, Right, Some, Success,
} from 'monet';
import LeftResponse from '../LeftResponse';

/**
 * @description Запрос на создание записи на прием.
 * @constructor
 */
function AppointmentRequest() {
  /**
   * @description Получить представление запроса.
   * @returns {Object}
   */
  this.toQuery = () => ({});
}

/**
 * @description Запрос на создание записи с новым пациентом.
 * @param {number}   advertisingChannelId Идентификатор рекламного канала.
 * @param {string}   birthAt              Дата рождения.
 * @param {string}   date                 Дата.
 * @param {number}   employeeId           Идентификатор сотрудника.
 * @param {string}   endsTime             Время окончания.
 * @param {boolean}  isInitial            Первичный прием.
 * @param {string}   name                 Имя.
 * @param {string}   note                 Примечание.
 * @param {string}   patronymic           Отчество.
 * @param {string}   phone                Номер телефона.
 * @param {string}   phoneType            Тип номера телефона.
 * @param {number[]} serviceIds           Список идентификаторов услуг.
 * @param {string}   startsTime           Время начала.
 * @param {string}   surname              Фамилия.
 * @constructor
 */
export function AppointmentNewPatientCreationRequest(
  advertisingChannelId,
  birthAt,
  date,
  employeeId,
  endsTime,
  isInitial,
  name,
  note,
  patronymic,
  phone,
  phoneType,
  serviceIds,
  startsTime,
  surname,
) {
  /**
   * @description Получить идентификатор сотрудника.
   * @returns {number}
   */
  this.getEmployeeId = () => employeeId;

  /**
   * @inheritDoc
   */
  this.toQuery = () => ({
    advertising_channel_id: advertisingChannelId,
    birth_at: birthAt,
    date,
    employee_id: employeeId,
    ends_time: endsTime,
    is_initial: isInitial,
    name,
    note,
    patronymic,
    phone,
    phone_type: phoneType,
    service_ids: serviceIds,
    starts_time: startsTime,
    surname,
  });
}

AppointmentNewPatientCreationRequest.prototype = AppointmentRequest.prototype;

/**
 * @description Запрос на создание записи с существующим пациентом.
 * @param {number}   advertisingChannelId Идентификатор рекламного канала.
 * @param {string}   date                 Дата.
 * @param {number}   employeeId           Идентификатор сотрудника.
 * @param {string}   endsTime             Время окончания.
 * @param {boolean}  isInitial            Первичный прием.
 * @param {string}   note                 Примечание.
 * @param {number}   patientId            Идентификатор пациента.
 * @param {number[]} serviceIds           Идентификаторы услуг.
 * @param {string}   startsTime           Время начала.
 * @param {number}   waitingId            Идентификатор записи из листа ожидания.
 * @param {number}   crmDealId            Идентификатор CRM-сделки.
 * @constructor
 */
export function AppointmentExistPatientCreationRequest(
  advertisingChannelId,
  date,
  employeeId,
  endsTime,
  isInitial,
  note,
  patientId,
  serviceIds,
  startsTime,
  waitingId,
  crmDealId,
) {
  /**
   * @description Получить дату визита.
   * @returns {string}
   */
  this.getDate = () => date;

  /**
   * @description Получить идентификатор сотрудника.
   * @returns {number}
   */
  this.getEmployeeId = () => employeeId;

  /**
   * @description Получить идентификатор пациента.
   * @returns {number}
   */
  this.getPatientId = () => patientId;

  /**
   * @inheritDoc
   */
  this.toQuery = () => ({
    advertising_channel_id: advertisingChannelId,
    date,
    employee_id: employeeId,
    ends_time: endsTime,
    is_initial: isInitial,
    note,
    patient_id: patientId,
    service_ids: serviceIds,
    starts_time: startsTime,
    waiting_id: waitingId,
    crm_deal_id: crmDealId,
  });
}

AppointmentExistPatientCreationRequest.prototype = AppointmentRequest.prototype;

/**
 * @description Запрос на создание записи по заявке из агрегатора.
 * @param {number}             orderId Идентификатор заявки.
 * @param {AppointmentRequest} request Запрос.
 * @constructor
 */
export function AggregatorAppointmentRequest(orderId, request) {
  if (Object.prototype.isPrototypeOf.call(AppointmentRequest.prototype, request) !== true) {
    throw new Error('Request is not AppointmentRequest');
  }
  Object.setPrototypeOf(this, request);

  /**
   * @inheritDoc
   */
  this.toQuery = () => ({
    aggregator_appointment_order_id: orderId,
    ...request.toQuery(),
  });
}

/**
 * @description Запрос на создание записи по звонку.
 * @param {number}             orderId Идентификатор заявки.
 * @param {AppointmentRequest} request Запрос.
 * @constructor
 */
export function TelephonyCallAppointmentRequest(callId, request) {
  if (Object.prototype.isPrototypeOf.call(AppointmentRequest.prototype, request) !== true) {
    throw new Error('Request is not AppointmentRequest');
  }
  Object.setPrototypeOf(this, request);

  /**
   * @inheritDoc
   */
  this.toQuery = () => ({
    call_id: callId,
    ...request.toQuery(),
  });
}

/**
 * @description Запрос на перемещение записи на прием.
 * @param {number} appointmentId Идентификатор записи на прием.
 * @param {*} employeeId         Идентификатор сотрудника.
 * @param {*} startsAt           Дата и время начала.
 * @constructor
 */
export function MoveAppointmentRequest(appointmentId, employeeId, startsAt) {
  this.getAppointmentId = () => appointmentId;
  this.toQuery = () => ({
    employee_id: employeeId,
    starts_at: startsAt,
  });
}

/**
 * @description Сотрудник.
 * @param {number} id         Идентификатор.
 * @param {string} name       Фамилия с инициалами.
 * @param {string} positions  Должности.
 * @param {number} timeStep   Шаг приема
 * @param {object} workPeriod Рабочий период
 * @constructor
 */
function Employee(
  id,
  name,
  positions,
  timeStep,
  workPeriod = {},
) {
  this.id = id;
  this.name = name;
  this.positions = positions;
  this.text = `${name} ${positions}`;
  this.timeStep = timeStep;
  this.workPeriod = workPeriod;
}

/**
 * @description Запрос на редактирование перерыва сотрудника.
 * @param {number} breakId Идентификатор перерыва.
 * @param {number} employeeId Идентификатор сотрудника.
 * @param {string} date Дата.
 * @param {string} endsTime Время окончания.
 * @param {string} startsTime Время начала.
 * @param {string} note Примечание.
 * @constructor
 */
export function EditEmployeeBreakRequest(breakId, employeeId, date, endsTime, startsTime, note) {
  this.getBreakId = () => breakId;
  this.getEmployeeId = () => employeeId;
  this.toQuery = () => ({
    date,
    ends_time: endsTime,
    note,
    starts_time: startsTime,
  });
}

/**
 * @description Шлюз распорядка записей на прием.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function AppointmentScheduleGateway(axios) {
  /**
   * @description Добавить перерыв сотруднику.
   * @param {number} employeeId Идентификатор сотрудника.
   * @param {string} day        День.
   * @param {any} request       Запрос.
   * @returns {Promise<Either>}
   */
  this.addBreak = async (employeeId, day, request) => {
    try {
      const response = await axios.post(
        `/api/employees/${employeeId}/appointment-days/${day}/break`,
        request,
      );
      return Right(response.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Получить список сотрудников со включенным расписанием.
   * @param {string}  date           Дата.
   * @param {string}  period         Период.
   * @returns {Promise<Maybe>}
   */
  this.viewEmployeeListWithEnabledScheduling = (date, period) => this.viewWorkingEmployeeList(
    date,
    period,
    true,
    true,
  );

  /**
   * @description Получить новые записи пациента для печати
   * @param patientId
   * @returns {Promise<Either>}
   */
  this.viewFutureAppointmentsPrint = (patientId) => axios
    .get(`/api/patients/${patientId}/future-appointments/print`)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список работающих сотрудников.
   * @param {string}  date           Дата.
   * @param {string}  period         Период.
   * @param {boolean} withDismissed  Показывать уволенных.
   * @param {boolean} withScheduling Показывать включенных в расписание.
   * @returns {Promise<Maybe>}
   */
  this.viewWorkingEmployeeList = async (
    date,
    period,
    withDismissed = true,
    withScheduling = false,
  ) => {
    const params = { period };
    if (withDismissed === false) {
      params.dismissed = 0;
    }
    if (withScheduling === true) {
      params.scheduling = 1;
    }
    const response = await axios.get(`/api/employees/working/${date}`, { params });
    const list = (response.data.data || []).map((item) => new Employee(
      item.id,
      item.name,
      item.positions,
      item.time_step,
    ));
    return Some(list);
  };

  /**
   * @description Получить список работающих сотрудников.
   * @param {string} dateStarts Дата начала.
   * @param {string} dateEnds   Дата окончания.
   * @returns {Promise<Maybe>}
   */
  this.viewWorkingEmployeeListByInterval = async (dateStarts, dateEnds) => {
    const params = { start: dateStarts, end: dateEnds };
    const response = await axios.get('/api/employees/working', { params });
    const list = (response.data.data || []).map((item) => new Employee(
      item.id,
      item.name,
      item.positions,
    ));
    return Some(list);
  };

  /**
   * @description Получить список работающих сотрудников со свободными датами на неделю вперед.
   * @param {Object} request Запрос.
   * @returns {Promise<Maybe>}
   */
  this.viewFreeDoctors = async (request) => axios.get('/api/employee-free-dates/week', { params: request });

  /**
   * @description Получить список работающих сотрудников в кабинете.
   * @param {number} cabinetId Идентификатор кабинета.
   * @param {string} date      Дата.
   * @param {string} period    Период.
   * @returns {Promise<Maybe>}
   */
  this.viewWorkingEmployeeListByCabinet = async (cabinetId, date, period) => {
    const params = { period };
    const response = await axios
      .get(
        `/api/cabinets/${cabinetId}/employees/working/${date}`,
        { params },
      );
    const list = (response.data.data || []).map((item) => new Employee(
      item.id,
      item.name,
      item.positions,
      null,
      item.work_period,
    ));
    return Some(list);
  };

  /**
   * @description Получить список рабочих дней клиники.
   * @param {boolean} filterNow Фильтровать дни от текущего.
   * @param {string}  month     Год и месяц.
   * @returns {Promise<month>}
   */
  this.viewClinicWorkDays = async (filterNow, month = 'now') => {
    const params = {
      year_and_month: month,
    };
    if (filterNow) {
      params.now = 1;
    }
    const response = await axios.get('/api/clinic/work-days', { params });
    return Some(response.data.data);
  };

  /**
   * Подготовить параметры для просмотра графика на день.
   * @param {number} step
   * @returns {any}
   */
  const prepareViewByDayParams = (step) => {
    const params = {};
    if (typeof step === 'number' && step) {
      params.step = step;
    }
    return params;
  };

  /**
   * @description Получить данные для отображения графика на день.
   * @param {string} date    Дата.
   * @param {number} step    Шаг.
   * @returns {Promise<Maybe>}
   */
  this.getViewByDay = async (date, step) => {
    const response = await axios.get(
      `/api/appointment-days/${date}`,
      { params: prepareViewByDayParams(step) },
    );
    return Some(response.data);
  };

  /**
   * @description Получить расширенные данные для отображения графика на день.
   * @param {string} date    Дата.
   * @returns {Promise<Maybe>}
   */
  this.getExpandedViewByDay = async (date) => {
    const response = await axios.get(`/api/expanded-appointment-days/${date}`);
    const list = (response.data.data || []).map((item) => ({
      ageTitle: item.age_title,
      id: item.id,
      labelIds: item.label_ids,
      labelNames: item.label_names,
      medcard: item.medcard,
      patientPhone: item.patient_phone,
      serviceNames: item.service_names,
      birthDate: item.birth_date,
    }));
    return Some(list);
  };

  /**
   * @description Получить пациентов для записи на прием.
   * @returns {Promise<Maybe>}
   */
  this.getPatientListFromAppointment = async (value = '') => {
    if (!value) {
      // eslint-disable-next-line no-param-reassign
      value = '';
    }
    const response = await axios.get(`/api/appointment-patients/${value}`);
    const formatPhone = (phone) => phone.replace(
      /(\d)(\d{3})(\d{3})(\d{2})(\d{2})/,
      '$1 ($2) $3-$4-$5',
    );
    const list = (response.data.data || []).map((item) => ({
      advertisingChannelId: item.advertising_channel_id,
      advertisingChannelName: item.advertising_channel_name,
      id: item.id,
      labels: item.labels,
      medcard: item.medcard,
      name: item.name,
      phone: item.phone,
      surnameWithInitials: item.surname_with_initials,
      text: `${item.name} ${formatPhone(item.phone)}`,
      visitCount: item.visit_count,
      notInitial: item.not_initial,
    }));
    return Some(list);
  };
  /**
   * @description Получить график в виде таблицы на день.
   * @param {string} date Дата.
   * @param {number} step Шаг.
   * @returns {void}
   */
  this.getSheetViewByDay = async (date, step) => {
    const response = await axios.get(
      `/api/appointment-days/${date}`,
      {
        headers: { accept: 'application/vnd.ms-excel' },
        params: prepareViewByDayParams(step),
        responseType: 'blob',
      },
    );
    return Some(response.data);
  };

  /**
   * Подготовить параметры для просмотра графика на неделю.
   * @param {string} period
   * @param {number} step
   * @returns {any}
   */
  const prepareViewByRangeParams = (period, step) => {
    const params = {};
    if (typeof step === 'number' && step) {
      params.step = step;
    }
    if (period) {
      params.period = period;
    }
    return params;
  };

  /**
   * @description Восстановление записи на прием.
   * @param {number}                                 appointmentId Идентификатор записи на прием.
   * @param {AppointmentExistPatientCreationRequest} request       Запрос.
   * @returns {Promise<Either>}
   */
  this.restore = async (appointmentId, request) => {
    const data = request.toQuery();
    try {
      const response = await axios.post(`/api/appointments/${appointmentId}/restore`, data);
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response.status === 400 || error.response.status === 403) {
          return Left(error.response.data);
        }
      }
      throw error;
    }
  };

  /**
   * @description Отправить запрос на установку временного шага расписания.
   * @param {number} step Шаг.
   * @returns {Promise<Either>}
   */
  this.setTimetableStep = (step) => axios
    .put('/api/timetable-step', { step })
    .then(
      async (response) => Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные для отображения графика на неделю.
   * @param {string} day        День.
   * @param {number} employeeId Идентификатор сотрудника.
   * @param {string} period     Дата.
   * @param {number} step       Шаг.
   * @returns {Promise<Maybe>}
   */
  this.getViewByRange = async (day, employeeId, period, step) => {
    const response = await axios.get(
      `/api/employees/${employeeId}/appointment-range/${day}`,
      { params: prepareViewByRangeParams(period, step) },
    );
    const data = {
      actions: response.data.actions || [],
      cabinets: response.data.cabinets || [],
      employee: {
        actions: response.data.employee_actions,
        days: response.data.days,
        id: response.data.employee_id,
        name: response.data.employee_name,
        positions: response.data.employee_positions,
      },
      nearAppointments: response.data.near_appointments,
      stepMinutes: response.data.step_minutes,
      timeline: response.data.timeline,
      title: response.data.title,
    };
    return Some(data);
  };

  /**
   * @description Получить данные для отображения расширенного графика на неделю.
   * @param {string} day        День.
   * @param {number} employeeId Идентификатор сотрудника.
   * @param {string} period     Дата.
   * @returns {Promise<Maybe>}
   */
  this.getExpandedViewByRange = async (day, employeeId, period) => {
    const response = await axios.get(
      `/api/employees/${employeeId}/expanded-appointment-range/${day}`,
      { params: prepareViewByRangeParams(period) },
    );
    const list = (response.data.data || []).map((item) => ({
      ageTitle: item.age_title,
      id: item.id,
      labelIds: item.label_ids,
      labelNames: item.label_names,
      medcard: item.medcard,
      patientPhone: item.patient_phone,
      serviceNames: item.service_names,
      birthDate: item.birth_date,
    }));
    return Some(list);
  };

  /**
   * @description Получить данные для отображения графика на месяц.
   * @param {number} employeeId Идентификатор сотрудника.
   * @param yearAndMonth
   * @returns {Promise<Maybe>}
   */
  this.getViewByMonth = async (employeeId, yearAndMonth) => {
    const response = await axios.get(
      `/api/employees/${employeeId}/appointment-month/${yearAndMonth}`,
    );
    return Some(response.data);
  };

  /**
   * @description Получить данные для отображения графика по кабинету на месяц.
   * @returns {Promise<Maybe>}
   * @param cabinetId
   * @param yearAndMonth
   */
  this.getViewByCabinetMonth = async (cabinetId, yearAndMonth) => {
    const response = await axios.get(
      `/api/cabinets/${cabinetId}/appointment-month/${yearAndMonth}`,
    );
    return Some(response.data);
  };

  /**
   * @description Получить график в виде таблицы на неделю.
   * @param {string} day        День.
   * @param {number} employeeId Идентификатор сотрудника.
   * @param {string} period     Дата.
   * @param {number} step       Шаг.
   * @returns {Promise<Maybe>}
   */
  this.getSheetViewByRange = async (day, employeeId, period, step) => {
    const response = await axios.get(
      `/api/employees/${employeeId}/appointment-range/${day}`,
      {
        headers: { accept: 'application/vnd.ms-excel' },
        params: prepareViewByRangeParams(period, step),
        responseType: 'blob',
      },
    );
    return Some(response.data);
  };

  /**
   * @description Создать новую заявку на прием с существующим пациентом.
   * @param {AppointmentExistPatientCreationRequest} creationRequest Запрос на создание.
   * @returns {Promise<Either>}
   */
  this.createWithExistPatient = async (creationRequest) => {
    try {
      const response = await axios.post(
        `/api/patients/${creationRequest.getPatientId()}/appointments`,
        creationRequest.toQuery(),
      );
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response.status === 400 || error.response.status === 403) {
          return Left(error.response.data);
        }
      }
      throw error;
    }
  };

  /**
   * @description Создать новую заявку на прием с новым пациентом.
   * @param {AppointmentNewPatientCreationRequest} creationRequest Запрос на создание.
   * @returns {Promise<Either>}
   */
  this.createWithNewPatient = async (creationRequest) => {
    try {
      const response = await axios.post(
        `/api/employees/${creationRequest.getEmployeeId()}/appointments`,
        creationRequest.toQuery(),
      );
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response.status === 400 || error.response.status === 403) {
          return Left(error.response.data);
        }
      }
      throw error;
    }
  };

  /**
   * @description Редактировать заявку на прием с новым пациентом.
   * @param {Number}                               employeeId  Идентификатор сотрудника.
   * @param {AppointmentNewPatientCreationRequest} editRequest Запрос на редактирование.
   * @param {Number}                               visitId     Идентификатор визита.
   * @returns {Promise<Either>}
   */
  this.editWithNewPatient = async (employeeId, editRequest, visitId) => {
    try {
      const response = await axios.put(
        `/api/employees/${employeeId}/appointments/${visitId}/new-patient`,
        editRequest.toQuery(),
      );
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response.status === 400 || error.response.status === 403) {
          return Left(error.response.data);
        }
      }
      throw error;
    }
  };

  /**
   * @description Редактировать заявку на прием с существующим пациентом.
   * @param {AppointmentExistPatientCreationRequest} editRequest Запрос на редактирование.
   * @param {Number}                                 visitId     Идентификатор визита.
   * @returns {Promise<Either>}
   */
  this.editWithExistPatient = async (editRequest, visitId) => {
    try {
      const response = await axios
        .put(`/api/appointments/${visitId}`, editRequest.toQuery());
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        if (error.response.status === 400 || error.response.status === 403) {
          return Left(error.response.data);
        }
      }
      throw error;
    }
  };
  /**
   * @description Пациент пришел на прием.
   * @param {number} appointmentId Идентификатор записи на прием.
   * @returns {Promise<Either>}
   */
  this.came = async (appointmentId) => {
    try {
      const response = await axios.put(
        `/api/appointments/${appointmentId}/came`,
      );
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отменить запись на прием.
   * @param {number} appointmentId         Идентификатор записи на прием.
   * @param {string} cause                 Комментарий.
   * @param {string} rejectionReasonId     Причина отмены.
   * @param {string} customRejectionReason Уникальная причина отмены.
   * @returns {Promise<Either>}
   */
  this.cancel = async (
    appointmentId,
    cause,
    rejectionReasonId,
    customRejectionReason,
  ) => {
    try {
      const response = await axios.put(
        `/api/appointments/${appointmentId}/cancel`,
        {
          cause,
          rejection_reason: rejectionReasonId,
          custom_rejection_reason: customRejectionReason,
        },
      );
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отменить заявку на прием от медицинского агрегатора.
   * @param {number} orderId Идентификатор заявки.
   * @returns {Promise<Either>}
   */
  this.cancelAggregatorOrder = async (orderId) => axios
    .put(`/api/aggregator-appointment-orders/${orderId}/cancel`)
    .then(
      (response) => Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Подтвердить приход пациента.
   * @param {number} appointmentId Идентификатор записи на прием.
   * @returns {Promise<Either>}
   */
  this.confirm = async (appointmentId) => {
    try {
      const response = await axios.put(
        `/api/appointments/${appointmentId}/confirm`,
      );
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Проставить статус "Не подтверждено"
   * @param {number} appointmentId Идентификатор записи на прием.
   * @returns {Promise<Either>}
   */
  this.noConfirm = async (appointmentId) => {
    try {
      const response = await axios.put(
        `/api/appointments/${appointmentId}/noconfirm`,
      );
      return Right(response.data.message);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Получить заявку на прием от медицинского агрегатора.
   * @param {number} orderId Идентификатор заявки.
   * @returns {Promise<Either>}
   */
  this.getAggregatorOrder = (orderId) => axios
    .get(`/api/aggregator-appointment-orders/${orderId}`)
    .then(
      async (response) => Right(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить заявки на прием сгруппированные по агрегаторам.
   * @returns {Promise<Maybe>}
   */
  this.getAggregatorOrders = () => axios
    .get('/api/aggregator-appointment-orders')
    .then(async (response) => Maybe.fromEmpty(response.data));

  /**
   * @description Получить ближайшие неподтвержденные записи
   * @returns {Promise<Maybe>}
   */
  this.getNearestUnconfirmedAppointments = () => axios
    .get('/api/nearest-unconfirmed-appointments')
    .then(async (response) => Maybe.fromEmpty(response.data));

  /**
   * @description Получить количество заявок на прием по агрегаторам.
   * @returns {Promise<Maybe>}
   */
  this.getAggregatorOrdersCount = () => axios
    .get('/api/aggregator-appointment-orders/count')
    .then(async (response) => Maybe.fromEmpty(response.data));

  /**
   * @description Получить данные записи на прием для редактирования по идентификатору.
   * @param {number} employeeId    Идентификатор сотрудника.
   * @param {number} appointmentId Идентификатор записи.
   * @returns {Promise<Either>}
   */
  this.getById = async (employeeId, appointmentId) => {
    try {
      const response = await axios.get(
        `/api/employees/${employeeId}/appointments/${appointmentId}`,
      );
      return Right({
        actions: response.data.actions || [],
        advertisingChannel: {
          id: response.data.advertising_channel_id,
          name: response.data.advertising_channel_name,
        },
        id: appointmentId,
        isInitial: response.data.is_initial,
        note: response.data.note,
        patientId: response.data.patient_id,
        patientLabels: response.data.patient_labels,
        patientName: response.data.patient_surname_with_initials,
        patientFullName: response.data.patient_full_name,
        status: response.data.status,
        timeEnds: response.data.time_ends,
        timeStarts: response.data.time_starts,
        isConfirm: response.data.is_confirm,
        services: (response.data.services || [])
          .map((item) => ({
            duration: item.duration,
            text: `${item.name}, ${item.duration}`,
            title: item.name,
            value: item.id,
          })),
        visitId: response.data.visit_id,
        waitingIds: [], // (response.data.waitings || []).map(({ id }) => id),
      });
    } catch (error) {
      if (error.isAxiosError && error.response.status === 404) {
        return Left(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Получить список записей на прием пациента.
   * @param {number} patientId Идентификатор пациента.
   * @param {String} filterType Тип фильтрации
   * @param {String} sortByDate Сортировать по дате
   * @returns {Promise<Either>}
   */
  this.viewFilteredAppointmentListByPatientId = async (patientId, filterType, sortByDate) => axios
    .get(
      `/api/patients/${patientId}/filtered-appointments`,
      {
        params:
          {
            filter_type: filterType,
            sort_by_date: sortByDate,
          },
      },
    )
    .then(
      async (response) => {
        const makeAppointment = (item) => ({
          actions: item.actions || [],
          cause: item.cause || '',
          date: item.date,
          employeeId: item.employee_id,
          employeeName: item.employee_name,
          employeeSpeciality: item.employee_speciality,
          endsTime: item.ends_time,
          id: item.id,
          startsTime: item.starts_time,
          status: item.status_label,
          statusTitle: item.status,
          rejectionReason: item.rejection_reason,
          note: item.note,
          dateTitle: item.date_title,
          branchName: item.branch_name,
        });
        const appointments = (response.data.data || []).map(makeAppointment);
        return Either.right({
          actions: response.data.actions,
          appointments,
          counts: response.data.counts,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Получить список отмененных записей на прием пациента.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise}
   */
  this.viewCanceledAppointmentListByPatientId = (patientId) => axios
    .get(`/api/patients/${patientId}/canceled-appointments`)
    .then(
      async (response) => {
        const makeAppointment = (item) => ({
          cause: item.cause || '-',
          date: item.date,
          employeeId: item.employee_id,
          employeeName: item.employee_name,
          employeePositions: item.employee_positions,
          endsTime: item.ends_time,
          id: item.id,
          startsTime: item.starts_time,
          status: item.status_label,
          statusTitle: item.status,
        });
        const appointments = (response.data.data || []).map(makeAppointment);
        return Either.right(appointments);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title, causes } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Получить временную шкалу.
   * @param {string}  date  Дата.
   * @param {number}  step  Шаг.
   * @param {boolean} isNow Убрать прошедшее время.
   * @returns {Promise<Either>}
   */
  this.viewTimeLine = async (date, step, isNow = false) => {
    const params = { step };
    if (isNow) {
      params.now = 1;
    }
    try {
      const response = await axios.get(`/api/appointment-timeline/${date}`, { params });
      return Right(response.data.data);
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        return Left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить временную шкалу сотрудника на день.
   * @param {number}  employeeId  Идентификатор сотрудника.
   * @param {string}  isoDate     Дата.
   * @param {string}  timeStarts  Время начала.
   * @param {boolean} filterByNow Отсекать прошедшие периоды.
   * @returns {Promise<Either>}
   */
  this.viewEmployeeTimeLine = (employeeId, isoDate, timeStarts, filterByNow = true) => axios
    .get(
      `/api/employees/${employeeId}/timeline/${isoDate}`,
      { params: { time: timeStarts, now: filterByNow ? 1 : 0 } },
    )
    .then(
      async (response) => Either.right({
        actuallyPeriods: response.data.actually_periods,
        timeline: response.data.timeline,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 404].includes(error.response.status)) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные для отображения графика по кабинетам на день.
   * @param {number[]} cabinetIds Идентификаторы кабинетов.
   * @param {number}   step       Шаг.
   * @param {string}   date       Дата.
   * @returns {Promise}
   */
  this.getViewByCabinetsForDay = async (cabinetIds, date, step) => {
    const getParams = () => {
      const params = {};
      if (typeof step === 'number' && step) {
        params.step = step;
      }
      if (cabinetIds && Array.isArray(cabinetIds)) {
        params.cabinet_ids = cabinetIds;
      }
      return params;
    };
    const response = await axios.get(
      `/api/cabinets/appointment-days/${date}`,
      { params: getParams() },
    );
    return Some(response.data);
  };

  /**
   * @description Получить данные для отображения графика по кабинетам на день.
   * @param {number[]} cabinetIds Идентификаторы кабинетов.
   * @param {string}   date       Дата.
   * @returns {Promise}
   */
  this.getExpandedViewByCabinetsForDay = async (cabinetIds, date) => {
    const getParams = () => {
      const params = {};
      if (cabinetIds && Array.isArray(cabinetIds)) {
        params.cabinet_ids = cabinetIds;
      }
      return params;
    };
    const response = await axios.get(
      `/api/cabinets/expanded-appointment-days/${date}`,
      { params: getParams() },
    );
    const list = (response.data.data || []).map((item) => ({
      employeeName: item.employee_name,
      ageTitle: item.age_title,
      id: item.id,
      labelIds: item.label_ids,
      labelNames: item.label_names,
      medcard: item.medcard,
      patientPhone: item.patient_phone,
      serviceNames: item.service_names,
    }));
    return Some(list);
  };

  /**
   * @description Получить данные для отображения графика по кабинетам на день.
   * @param {number} cabinetId Идентификаторы кабинетов.
   * @param {number} step      Шаг.
   * @param {string} date      Дата.
   * @param {string} period    Период.
   * @returns {Promise}
   */
  this.getViewByCabinetForRange = async (cabinetId, date, step, period) => {
    const response = await axios.get(
      `/api/cabinets/${cabinetId}/appointment-range/${date}`,
      { params: prepareViewByRangeParams(period, step) },
    );
    return Some(response.data);
  };

  /**
   * @description Получить данные для отображения графика по кабинетам на день.
   * @param {number} cabinetId Идентификаторы кабинетов.
   * @param {string} date      Дата.
   * @param {string} period    Период.
   * @returns {Promise}
   */
  this.getExpandedViewByCabinetForRange = async (cabinetId, date, period) => {
    const response = await axios.get(
      `/api/cabinets/${cabinetId}/expanded-appointment-range/${date}`,
      { params: prepareViewByRangeParams(period) },
    );
    const list = (response.data.data || []).map((item) => ({
      employeeName: item.employee_name,
      ageTitle: item.age_title,
      id: item.id,
      labelIds: item.label_ids,
      labelNames: item.label_names,
      medcard: item.medcard,
      patientPhone: item.patient_phone,
      serviceNames: item.service_names,
    }));
    return Some(list);
  };

  /**
   * @description Получить график по кабинетам в виде таблицы на день.
   * @param {number[]} cabinetIds День.
   * @param {number}   date       Идентификатор сотрудника.
   * @param {string}   step       Дата.
   * @returns {Promise<Maybe>}
   */
  this.getSheetViewCabinetsByDay = async (cabinetIds, date, step) => {
    const getParams = () => {
      const params = {};
      if (typeof step === 'number' && step) {
        params.step = step;
      }
      if (cabinetIds && Array.isArray(cabinetIds)) {
        params.cabinet_ids = cabinetIds;
      }
      return params;
    };
    const response = await axios.get(
      `/api/cabinets/appointment-days/${date}`,
      {
        headers: { accept: 'application/vnd.ms-excel' },
        params: getParams(),
        responseType: 'blob',
      },
    );
    return Some(response.data);
  };

  /**
   * @description Получить график кабинета в виде таблицы на неделю.
   * @param {number} cabinetId Идентификаторы кабинетов.
   * @param {string} date      Дата.
   * @param {number} step      Шаг.
   * @param {string} period    Период.
   * @returns {Promise<Maybe>}
   */
  this.getSheetViewCabinetByRange = async (cabinetId, date, step, period) => {
    const response = await axios.get(
      `/api/cabinets/${cabinetId}/appointment-range/${date}`,
      {
        headers: { accept: 'application/vnd.ms-excel' },
        params: prepareViewByRangeParams(period, step),
        responseType: 'blob',
      },
    );
    return Some(response.data);
  };
  /**
   * @description Переместить запись на прием.
   * @param {MoveAppointmentRequest} request Запрос.
   * @returns {Promise}
   */
  this.moveAppointment = (request) => axios
    .put(`/api/appointments/${request.getAppointmentId()}/move`, request.toQuery())
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError) {
          if (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          ) {
            return Fail(error.response.data);
          }
        }
        throw error;
      },
    );
  /**
   * @description Просмотр расписания сотрудника на день.
   * @param {string} day        День.
   * @param {number} employeeId Идентификатор сотрудника.
   * @returns {Promise}
   */
  this.viewEmployeeAppointmentsDay = (day, employeeId) => axios
    .get(`/api/employees/${employeeId}/appointment-day/${day}`)
    .then(
      async (response) => Success(response.data),
      async (error) => {
        if (error.isAxiosError) {
          if (
            error.response.status === 400
            || error.response.status === 403
            || error.response.status === 404
          ) {
            return Fail(error.response.data);
          }
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование перерыва сотрудника.
   * @param {EditEmployeeBreakRequest} request
   * @returns {Promise<Either<any,LeftResponse>>}
   */
  this.editEmployeeBreak = (request) => axios
    .put(
      `/api/breaks/${request.getBreakId()}`,
      request.toQuery(),
    ).then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.Left(response);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на отмену перерыва сотрудника.
   * @param {number} breakId
   * @returns {Promise<Either<any,LeftResponse>>}
   */
  this.cancelEmployeeBreak = (breakId) => axios
    .delete(`/api/breaks/${breakId}`)
    .then(
      async (response) => Either.Right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.Left(response);
        }
        throw error;
      },
    );
  /**
   * @description Получить расписание врача для печати.
   *
   * @param {number} employeeId Идентификатор сотрудника.
   * @param {string} date Дата в формате YYYY-MM-DD
   * @param {boolean} hidePatientPhoneNumber Скрыть номер телефона пациента.
   * @returns {object}
   */
  this.viewAppointmentScheduleEmployeePrint = (employeeId, date, hidePatientPhoneNumber) => axios
    .get(
      `/api/appointments/${employeeId}/print/${date}`,
      { params: { hide_patient_phone_number: Number(hidePatientPhoneNumber) } },
    )
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Показать настройки.
   * @returns {Promise<Either>}
   */
  this.viewSettings = () => axios
    .get('/api/appointment-settings')
    .then(
      (response) => Either.right(response.data.data),
      (error) => {
        if (error.isAxiosError && error.response.status === 403) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Обновить настройки расписания.
   * @param {string} colorMode
   * @returns {Promise<Either>}
   */
  this.updateAppointmentSettings = (colorMode) => axios
    .put('/api/appointment-settings', { appointment_color_mode: colorMode })
    .then(
      (response) => Either.right(response.data.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Обновить цвет кресла.
   * @param {number} cabinetId
   * @param {string} color
   * @returns {Promise<Either>}
   */
  this.updateCabinetColor = (cabinetId, color) => axios
    .put(`/api/cabinets/${cabinetId}/appointment-color`, { color })
    .then(
      (response) => Either.right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Обновить цвет сотрудника.
   * @param {number} employeeId
   * @param {string} color
   * @returns {Promise<Either>}
   */
  this.updateEmployeeColor = (employeeId, color) => axios
    .put(`/api/employees/${employeeId}/appointment-color`, { color })
    .then(
      (response) => Either.right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.left(error.response.data);
        }
        throw error;
      },
    );
}
